<script setup lang="ts">
import { PropType, computed } from 'vue';
import md5 from 'blueimp-md5';
import { AppEnums } from '@/shared/enums';
import nopictureImg from '@/assets/nopicture.png';
import { UserAvatar } from '@/stores/users/models';

const props = defineProps({
  avatar: {
    type: Object as PropType<UserAvatar>,
  },
  width: {
    type: Number,
    default: 200,
  },
  showInfo: {
    type: Boolean,
    default: true,
  },
  isCentered: {
    type: Boolean,
    default: true,
  },
});

const gravatarImageUrl = computed(() => {
  if (!props.avatar?.avatarUrl) return nopictureImg;
  return `https://www.gravatar.com/avatar/${md5(props.avatar.avatarUrl)}?s=140`;
});
</script>

<template>
  <div class="flex gap-2 pt-1" :class="{ 'items-center': isCentered }">
    <template v-if="avatar">
      <template v-if="avatar.imageBase64">
        <Avatar
          :image="avatar.imageBase64"
          class="mr-2 w-[32px] h-[32px] min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px]"
          shape="circle"
        />
      </template>
      <template v-else-if="avatar.avatarType === AppEnums.AvatarTypes.Gravatar && avatar.avatarUrl">
        <img :src="gravatarImageUrl" class="w-[32px] h-[32px] shadow-md rounded-full border dark:border-surface-700 mr-2" />
      </template>
      <template v-else>
        <img :src="nopictureImg" class="w-[32px] h-[32px] shadow-md rounded-full border dark:border-surface-700 mr-2" />
      </template>

      <div class="flex flex-col" v-if="showInfo">
        <div
          class="text-[14px] font-medium text-gray-900 dark:text-gray-300 whitespace-nowrap overflow-hidden overflow-ellipsis"
          :style="{ width: width + 'px' }"
        >
          {{ avatar.fullName }}
        </div>
        <div
          class="text-[14px] font-normal text-gray-600 dark:text-gray-500 whitespace-nowrap overflow-hidden overflow-ellipsis"
          :style="{ width: width + 'px' }"
          v-tooltip.bottom="avatar.email"
        >
          {{ avatar.email }}
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex w-full gap-5">
        <div class="w-[45px] h-[45px]">
          <Skeleton shape="circle" size="42px"></Skeleton>
        </div>
        <div class="flex flex-col w-full mr-2">
          <div class="text-[14px] pr-6 font-medium text-gray-900 dark:text-gray-300">
            <Skeleton class="mb-2"></Skeleton>
          </div>
          <div class="text-[14px] pr-6 font-normal text-gray-600 dark:text-gray-500">
            <Skeleton class="mb-2"></Skeleton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
